import ApiService from './api.service';
import type { PaginationParams, ValidationResult } from '@/types';

/**
 * Project notification settings interface
 */
export interface NotificationSettings {
  emailNotificationsEnabled: boolean;
  balanceLimitNotificationsEnabled: boolean;
  balanceLimitThreshold: number;
}

/**
 * Email type preference interface
 */
export interface EmailTypePreference {
  emailType: string;
  enabled: boolean;
}

/**
 * Project notification query parameters
 */
export interface NotificationQueryParams extends PaginationParams {
  sort?: string;
}

/**
 * Service for managing project notification settings
 */
class ProjectNotificationsService extends ApiService {
  constructor() {
    super({
      showNotifications: true,
      notificationOptions: {
        title: 'Project Notifications Error'
      }
    });
  }

  /**
   * Get notification settings for a specific project
   * @param projectId - The project ID
   * @returns Promise that resolves with the notification settings
   */
  async getNotificationSettings(projectId: number): Promise<NotificationSettings | null> {
    return this.get(`/api/project/${projectId}/notifications`);
  }

  /**
   * Create notification settings for a project
   * @param projectId - The project ID
   * @param settings - The notification settings
   * @returns Promise that resolves with the created settings
   */
  async createNotificationSettings(projectId: number, settings: NotificationSettings): Promise<NotificationSettings | null> {
    return this.post(`/api/project/${projectId}/notifications`, settings);
  }

  /**
   * Update notification settings for a project
   * @param projectId - The project ID
   * @param settings - The updated notification settings
   * @returns Promise that resolves with the updated settings
   */
  async updateNotificationSettings(projectId: number, settings: Partial<NotificationSettings>): Promise<NotificationSettings | null> {
    return this.put(`/api/project/${projectId}/notifications`, settings);
  }

  /**
   * Delete notification settings for a project
   * @param projectId - The project ID
   * @returns Promise that resolves when the settings are deleted
   */
  async deleteNotificationSettings(projectId: number): Promise<void | null> {
    return this.delete(`/api/project/${projectId}/notifications`);
  }

  /**
   * Send a test notification for a project (admin only)
   * @param projectId - The project ID
   * @returns Promise that resolves when the test notification is sent
   */
  async sendTestNotification(projectId: number): Promise<void | null> {
    return this.post(`/api/admin/project/${projectId}/notifications/test`, {});
  }

  /**
   * Get email type preferences for a project
   * @param projectId - The project ID
   * @returns Promise that resolves with the email type preferences
   */
  async getEmailTypePreferences(projectId: number): Promise<EmailTypePreference[] | null> {
    return this.get(`/api/project/${projectId}/notifications/email-types`);
  }

  /**
   * Update a specific email type preference
   * @param projectId - The project ID
   * @param emailType - The email type to update
   * @param enabled - Whether to enable or disable the email type
   * @returns Promise that resolves when the preference is updated
   */
  async updateEmailTypePreference(projectId: number, emailType: string, enabled: boolean): Promise<void | null> {
    return this.put(`/api/project/${projectId}/notifications/email-types/${emailType}?enabled=${enabled}`, {});
  }

  /**
   * Check if a specific email type is enabled
   * @param projectId - The project ID
   * @param emailType - The email type to check
   * @returns Promise that resolves with the enabled status
   */
  async isEmailTypeEnabled(projectId: number, emailType: string): Promise<{ enabled: boolean } | null> {
    return this.get(`/api/project/${projectId}/notifications/email-types/${emailType}/status`);
  }

  /**
   * Get all notification settings (admin only)
   * @param params - Query parameters
   * @returns Promise that resolves with paginated notification settings
   */
  async getAllNotificationSettings(params: NotificationQueryParams = {}): Promise<{
    content: NotificationSettings[];
    totalElements: number;
    totalPages: number;
    size: number;
    number: number;
  } | null> {
    const queryParams = new URLSearchParams({
      page: (params.page || 0).toString(),
      size: (params.size || 20).toString(),
      sort: params.sort || 'projectId,asc'
    });
    
    return this.get(`/api/admin/notifications?${queryParams}`);
  }

  /**
   * Validate notification settings data
   * @param settings - The settings to validate
   * @returns Validation result with isValid and errors
   */
  validateSettings(settings: Partial<NotificationSettings>): ValidationResult {
    const errors: Record<string, string> = {};
    
    if (settings.balanceLimitNotificationsEnabled && 
        (!settings.balanceLimitThreshold || settings.balanceLimitThreshold < 0)) {
      errors.balanceLimitThreshold = 'Threshold amount is required and must be positive when balance limit notifications are enabled';
    }
    
    return {
      isValid: Object.keys(errors).length === 0,
      errors
    };
  }
}

export default new ProjectNotificationsService();
